import { useState, type FormEvent } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { ApiError } from '../api/client';
import { authApi } from '../api/endpoints';
import { useToast } from '../app/ToastProvider';
import { AuthShell } from './AuthShell';

const MIN_PASSWORD_LENGTH = 8;

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const navigate = useNavigate();
  const toast = useToast();

  const handleSubmit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});

    if (password.length < MIN_PASSWORD_LENGTH) {
      setFieldErrors({ password: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.` });
      return;
    }
    if (password !== confirmPassword) {
      setFieldErrors({ confirmPassword: 'Passwords do not match.' });
      return;
    }

    setSubmitting(true);
    try {
      const { message } = await authApi.resetPassword(token, password, confirmPassword);
      toast.success(message);
      navigate('/login', { replace: true });
    } catch (caught) {
      if (caught instanceof ApiError) {
        setFieldErrors(caught.byField());
        setError(caught.message);
      } else {
        setError('The password could not be reset. Please request a new link.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Reset password" subtitle="This reset link is incomplete.">
        <Stack spacing={2.5}>
          <Alert severity="error">
            The reset link is missing its token. Request a new password reset email.
          </Alert>
          <Link component={RouterLink} to="/forgot-password" variant="body2" align="center">
            Request a new reset link
          </Link>
        </Stack>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset password" subtitle="Choose a new password for your account.">
      <form onSubmit={handleSubmit} noValidate>
        <Stack spacing={2.5}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <TextField
            label="New Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={Boolean(fieldErrors.password)}
            helperText={fieldErrors.password ?? `At least ${MIN_PASSWORD_LENGTH} characters.`}
            autoComplete="new-password"
            autoFocus
            required
          />

          <TextField
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value)}
            error={Boolean(fieldErrors.confirmPassword)}
            helperText={fieldErrors.confirmPassword}
            autoComplete="new-password"
            required
          />

          <Button type="submit" variant="contained" size="large" disabled={submitting}>
            {submitting ? 'Updating…' : 'Update password'}
          </Button>

          <Link component={RouterLink} to="/login" variant="body2" align="center">
            Back to sign in
          </Link>
        </Stack>
      </form>
    </AuthShell>
  );
}
